<template>
  <Toaster />
  <router-view />
</template>

<script setup lang="ts">
import { useHead } from "@unhead/vue";
import { useTheme } from "vuetify";

/**
 * Inject Vuetify's theme stylesheet.
 *
 * Vuetify 3.7.7 cannot deliver this itself under unhead 2.x. Its theme plugin
 * (node_modules/vuetify/lib/composables/theme.mjs) looks for unhead on the app
 * and, when present, hands over the stylesheet as a *function*:
 *
 *     const entry = head.push(getHead)      // unhead v1 API
 *
 * unhead v2 accepts the call and creates the entry — which is why a
 * <style id="vuetify-theme-stylesheet"> element did exist — but it never
 * invokes the function, so the element shipped completely EMPTY, in dev and in
 * the prerendered build alike.
 *
 * Everything Vuetify paints from those custom properties therefore resolved to
 * nothing: --v-theme-surface, --v-theme-on-surface, --v-hover-opacity and the
 * rest were all undefined. Menus and select dropdowns rendered with a
 * transparent background over the page text beneath them, and the hover state
 * collapsed to solid black. That affected every overlay on the site, not one
 * page.
 *
 * `theme.styles` is a computed ref holding the generated CSS, and @unhead/vue
 * unwraps a getter, so passing one keeps the style in sync if the theme ever
 * changes at runtime. Reusing Vuetify's own element id means unhead dedupes
 * against the empty entry Vuetify pushed rather than adding a second tag.
 */
const theme = useTheme();

useHead({
  style: [
    {
      id: "vuetify-theme-stylesheet",
      textContent: () => theme.styles.value,
    },
  ],
});
</script>
